import { Accordion } from '../../components/accordion/accordion';
import { ModalService } from '../../components/modal/modal.service';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import type { FormGroup, FormVersionGroup, MixedFormsBreakdown } from './verify.mixed.forms';
import { CSS, Style } from './verify.mixed.forms.modal.style';

addStyles(Style);

function formGroupPoleCount(groups: FormGroup[]): number {
	return groups.reduce(
		(total, group) => total + group.versions.reduce((sum, version) => sum + version.poleLabels.length, 0),
		0
	);
}

function viewPoleList(poleLabels: string[], restOnVersion: number | null): MithrilVNode {
	return m(
		'ul',
		{ className: CSS.poleList },
		poleLabels.map((label) =>
			m('li', [
				label,
				restOnVersion != null ? ` ${Translate(T.VERIFY_MIXED_FORMS_REST_ON, { version: restOnVersion })}` : null,
			])
		)
	);
}

function viewVersionSubgroup(version: FormVersionGroup, restOnVersion: number | null): MithrilVNode {
	return m('div', { className: CSS.versionGroup }, [
		version.displayVersion != null ? m('div', { className: CSS.versionLabel }, `V${version.displayVersion}`) : null,
		viewPoleList(version.poleLabels, restOnVersion),
	]);
}

function viewFormGroup(group: FormGroup, restOnVersion: number | null): MithrilVNode {
	// A single version renders like before ("Form Name (V3)"); more than one version of the same
	// form stays as one group, with each version clearly broken out inside it (UFO-1732).
	if (group.versions.length === 1) {
		const [version] = group.versions;
		return m(
			'div',
			{ className: CSS.formGroup },
			Accordion.view(
				{
					title: group.displayName,
					subtitle: version.displayVersion != null ? `V${version.displayVersion}` : undefined,
					open: true,
				},
				[viewPoleList(version.poleLabels, restOnVersion)]
			)
		);
	}

	return m(
		'div',
		{ className: CSS.formGroup },
		Accordion.view(
			{ title: group.displayName, open: true },
			group.versions.map((version) => viewVersionSubgroup(version, restOnVersion))
		)
	);
}

function viewSection(
	title: string,
	description: string,
	groups: FormGroup[],
	restOnVersion: number | null
): MithrilVNode | null {
	if (groups.length === 0) {
		return null;
	}

	return m(
		'div',
		{ className: CSS.section },
		Accordion.view({ title, open: true }, [
			m('p', { className: CSS.sectionDescription }, description),
			...groups.map((group) => viewFormGroup(group, restOnVersion)),
		])
	);
}

function viewIntro(breakdown: MixedFormsBreakdown): MithrilVNode {
	return m('p', { className: CSS.intro }, [
		Translate(T.VERIFY_MIXED_FORMS_INTRO_PT1),
		m('b', Translate(T.VERIFY_MIXED_FORMS_INTRO_FORMS, { count: breakdown.totalFormCount })),
		Translate(T.VERIFY_MIXED_FORMS_INTRO_PT2),
		m('b', Translate(T.VERIFY_MIXED_FORMS_INTRO_POLES, { count: breakdown.totalPoleCount })),
		Translate(T.VERIFY_MIXED_FORMS_INTRO_PT3),
	]);
}

function viewBody(breakdown: MixedFormsBreakdown): MithrilVNode[] {
	const sections = [
		viewSection(
			Translate(T.VERIFY_MIXED_FORMS_DIFFERENT_FORM_TITLE, { count: formGroupPoleCount(breakdown.differentForm) }),
			Translate(T.VERIFY_MIXED_FORMS_DIFFERENT_FORM_DESCRIPTION),
			breakdown.differentForm,
			null
		),
		viewSection(
			Translate(T.VERIFY_MIXED_FORMS_DIFFERENT_VERSION_TITLE, {
				count: formGroupPoleCount(breakdown.differentVersionSameForm),
			}),
			Translate(T.VERIFY_MIXED_FORMS_DIFFERENT_VERSION_DESCRIPTION, {
				count: formGroupPoleCount(breakdown.differentVersionSameForm),
			}),
			breakdown.differentVersionSameForm,
			breakdown.primaryDisplayVersion
		),
	];

	return sections.filter((section): section is MithrilVNode => section != null);
}

function viewContent(jobName: string, breakdown: MixedFormsBreakdown): MithrilVNode {
	return m('div', [
		m('p', { className: CSS.jobName }, Translate(T.VERIFY_MIXED_FORMS_JOB_NAME, { jobName })),
		viewIntro(breakdown),
		...viewBody(breakdown),
	]);
}

export const VerifyMixedFormsModal = {
	open(jobName: string, breakdown: MixedFormsBreakdown): void {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		ModalService.open({
			title: T.VERIFY_MIXED_FORMS_TITLE,
			content: () => viewContent(jobName, breakdown),
			hasCancel: false,
			className: CSS.modal,
		});
	},
};
